/**
 * Admin mirror of `merryflix-backend/src/constants/proposalStructure.ts`.
 *
 * ── MUST stay in sync ──────────────────────────────────────────────
 * This file is a copy. Whenever you change a value here, change
 * the backend source AND `merryflix-frontend/src/lib/proposalStructure.ts`
 * to match. Tests live in
 * `merryflix-backend/tests/proposalStructure.test.ts`.
 * ────────────────────────────────────────────────────────────────────
 */

export type ProposalTier = 'FREE' | 'PRO';

export const SLIDE_LIMITS: Readonly<Record<ProposalTier, number>> = {
  FREE: 10,
  PRO: 30,
} as const;

export const MIN_SLIDE_COUNT = 5;

export type AnchorType = 'TITLE' | 'QUESTION' | 'FINAL';

export type StructureErrorCode =
  | 'TOO_FEW_SLIDES'
  | 'TOO_MANY_SLIDES'
  | 'BAD_FIRST_TYPE'
  | 'BAD_PENULTIMATE_TYPE'
  | 'BAD_LAST_TYPE';

export type StructureValidationResult =
  | { ok: true }
  | { ok: false; code: StructureErrorCode; message: string };

export type SlideLite = { type: string; order?: number };

export function toOrderedDeck<T extends SlideLite>(slides: T[]): T[] {
  return [...slides].sort((a, b) => {
    const ao = a.order ?? Number.MAX_SAFE_INTEGER;
    const bo = b.order ?? Number.MAX_SAFE_INTEGER;
    return ao - bo;
  });
}

export function protectedSlideIndices(length: number): {
  first: number;
  question: number;
  final: number;
} {
  return {
    first: 0,
    question: Math.max(1, length - 2),
    final: Math.max(2, length - 1),
  };
}

export function isProtectedIndex(length: number, index: number): boolean {
  if (length < MIN_SLIDE_COUNT) return false;
  const { first, question, final } = protectedSlideIndices(length);
  return index === first || index === question || index === final;
}

export function slideCountLimit(tier: ProposalTier): number {
  return SLIDE_LIMITS[tier];
}

export function validateDeckStructure<T extends SlideLite>(
  slides: T[],
  tier: ProposalTier,
): StructureValidationResult {
  const ordered = toOrderedDeck(slides);
  const len = ordered.length;

  if (len < MIN_SLIDE_COUNT) {
    return {
      ok: false,
      code: 'TOO_FEW_SLIDES',
      message: `Need at least ${MIN_SLIDE_COUNT} slides (Title + ${MIN_SLIDE_COUNT - 3} middle + Question + Final). You have ${len}.`,
    };
  }

  const upperBound = SLIDE_LIMITS[tier];
  if (len > upperBound) {
    return {
      ok: false,
      code: 'TOO_MANY_SLIDES',
      message: `${tier} users can have at most ${upperBound} slides. You have ${len}.`,
    };
  }

  const first = ordered[0];
  const penultimate = ordered[len - 2];
  const last = ordered[len - 1];

  if (first.type !== 'TITLE') {
    return {
      ok: false,
      code: 'BAD_FIRST_TYPE',
      message: 'Slide 1 must be a TITLE slide.',
    };
  }
  if (penultimate.type !== 'QUESTION') {
    return {
      ok: false,
      code: 'BAD_PENULTIMATE_TYPE',
      message: 'The second-to-last slide must be a QUESTION slide.',
    };
  }
  if (last.type !== 'FINAL') {
    return {
      ok: false,
      code: 'BAD_LAST_TYPE',
      message: 'The last slide must be a FINAL slide.',
    };
  }

  return { ok: true };
}
